import projects from '@/api/projects'
import * as types from '../mutation-types'
import Vue from 'vue'

const state = {
  personalized: [],
  alphabetical: [],
  name: {},
  slug: {},
  all: {},
  loaded: false,
  loading: false,
  selectedLanguage: 'en'
}
const getters = {
  categoriesLoaded: (state) => {
    return state.loaded
  },
  projectCategories: (state) => (includeSubCategories) => {
    if (!state.loaded) {
      return null
    }
    const categories = state.personalized.map((id) => state.all[id][state.selectedLanguage])

    if (includeSubCategories === true) {
      inflateSubCategories(categories)
    }

    return categories
  },
  projectCategoryNames: (state) => {
    return state.personalized.map((id) => state.all[id][state.selectedLanguage].name)
  },
  sortedProjectCategories: (state) => (includeSubCategories, userPreference) => {
    let categories = state.alphabetical.map((id) => state.all[id][state.selectedLanguage])

    categories = categories.filter(category => !category.isPrivate)

    if (userPreference) {
      if (Vue.prototype.$isEngCom) {
        categories = categories.filter(category => category.name !== 'Eng-Tips' && category.name !== 'Games')
      }
    }

    if (includeSubCategories === true) {
      inflateSubCategories(categories)
    }

    return categories
  },
  sortedProjectCategoryNames: (state) => {
    return state.alphabetical.map((id) => state.all[id][state.selectedLanguage].name)
  },
  categoryByID: (state) => (id) => {
    return state.all[id] ? state.all[id][state.selectedLanguage] : null
  },
  categoryByName: (state) => (name) => {
    const id = state.name[name]
    return id && state.all[id] ? state.all[id][state.selectedLanguage] : null
  },
  nameCategoryByID: (state) => (id) => {
    return state.all[id][state.selectedLanguage].name
  },
  categoryBySlug: (state) => (slug) => {
    const id = state.slug[slug]
    return id && state.all[id] ? state.all[id][state.selectedLanguage] : null
  },
  subCategoriesForID: (state) => (id) => {
    if (!state.all[id] || !state.all[id][state.selectedLanguage]) {
      return []
    }
    const category = state.all[id][state.selectedLanguage]
    const categoryIDs = category.subCategories || []
    return categoryIDs.map((id) => state.all[id][state.selectedLanguage]).filter((category) => !!category)
  },
  getSelectedLanguage: (state) => {
    return state.selectedLanguage
  }
}

const inflateSubCategories = function (categories) {
  categories.forEach((category, offset) => {
    if (!category.subCategories || category.subCategories.length === 0) {
      return
    }

    categories[offset] = Object.assign({}, category)
    categories[offset].subCategories = []

    for (let i = 0; i < category.subCategories.length; i++) {
      categories[offset].subCategories[i] = state.all[category.subCategories[i]][state.selectedLanguage]
    }
  })
}

/* eslint-disable prefer-promise-reject-errors */
const actions = {
  getCategories ({ commit, state }) {
    return new Promise((resolve, reject) => {
      // if we have already loaded categories into the store, return those
      if ((state.loaded || state.loading) && state.alphabetical.length > 0) {
        return resolve(state.alphabetical)
      } else {
        commit(types.UPDATE_LOADING, true)
      }
      projects.getCategories().then((response) => {
        const categories = response.data.result.categories
        commit(types.ADD_CATEGORIES, categories)
        commit(types.UPDATE_LOADING, false)
        resolve(categories)
      }, (error) => {
        commit(types.UPDATE_LOADING, false)
        reject(error)
      })
    })
  },
  getCategoriesByEvent ({ commit, state }, { eventSlug }) {
    return new Promise((resolve, reject) => {
      projects.getCategoriesByEvent(eventSlug).then((response) => {
        const categories = response.data.result.categories
        resolve(categories)
      }, (error) => {
        reject(error)
      })
    })
  },
  updateSelectedLanguage ({ commit }, newLanguage) {
    commit(types.UPDATE_SELECTED_LANGUAGE, newLanguage)
  },
  clearCategories ({ commit, dispatch }) {
    commit(types.CLEAR_CATEGORIES)
  }
}

const mutations = {
  [types.ADD_CATEGORIES] (state, categories) {
    const languageOptions = ['en']
    state.loaded = true
    categories.forEach((category) => {
      // make state.all a 2 dimensional array
      // now you need [categoryID][language] to get category details
      Vue.set(state.all, category.id, [])
      state.all[category.id].en = category
      state.slug[category.slug] = category.id
      state.name[category.name] = category.id
      if (category.languages) {
        category.languages.forEach((altLanguage) => {
          const categoryAlt = JSON.parse(JSON.stringify(category))
          categoryAlt.name = altLanguage.name
          categoryAlt.description = altLanguage.description
          state.all[category.id][altLanguage.language] = categoryAlt
          state.name[altLanguage.name] = category.id

          if (languageOptions.length < category.languages.length) {
            languageOptions.push(altLanguage.language)
          }
        })
      }
      if (category.subCategories && category.subCategories.length > 0) {
        category.subCategories.forEach((sub) => {
          // make state.all a 2 dimensional array
          // now you need [categoryID][language] to get category details
          Vue.set(state.all, sub.id, [])
          state.all[sub.id].en = sub
          state.slug[sub.slug] = sub.id
          state.name[sub.name] = sub.id
          if (sub.languages) {
            sub.languages.forEach((altLanguage) => {
              const categoryAlt = JSON.parse(JSON.stringify(sub))
              categoryAlt.name = altLanguage.name
              categoryAlt.description = altLanguage.description
              state.all[sub.id][altLanguage.language] = categoryAlt
              state.name[altLanguage.name] = sub.id
            })
          }
        })

        languageOptions.forEach((language) => {
          state.all[category.id][language].subCategories = category.subCategories.map((sub) => sub.id)
        })
      }
    })
    const onlyTopLevelParents = categories.filter(category => category.parentID === 0)
    state.personalized = onlyTopLevelParents.map((category) => category.id)
    state.alphabetical = onlyTopLevelParents.map((category) => category.id)
    languageOptions.forEach((language) => {
      state.alphabetical.sort(function (a, b) {
        if (state.all[a][language].name < state.all[b][language].name) {
          return -1
        }
        if (state.all[a][language].name > state.all[b][language].name) {
          return 1
        }

        return 0
      })
    })
  },
  [types.UPDATE_SELECTED_LANGUAGE] (state, newLanguage) {
    state.selectedLanguage = newLanguage
  },
  [types.UPDATE_LOADING] (state, isLoading) {
    state.loading = isLoading
  },
  [types.CLEAR_CATEGORIES] (state) {
    state.personalized = []
    state.alphabetical = []
    state.name = {}
    state.slug = {}
    state.all = {}
    state.loaded = false
    state.selectedLanguage = 'en'
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}
